import React, { useState, useEffect } from "react";
import { Row, Col, Card, Tag, Button, Modal, Tabs } from "antd";
import Table from 'components/Common/Table';
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import * as adminactions from "store/actions/AuthActions";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import ExcelUploader from "components/Shared/ExcelUploader";
import fileDownload from "js-file-download";

const { TabPane } = Tabs;

const ImportAttendance = () => {
  const [importHistory, setImportHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorDetails, setErrorDetails] = useState([]);

  const organization = useSelector((state) => state.auth.organization);

  const historyColumns = [
    {
      title: "Import Date",
      dataIndex: "created_at",
      key: "created_at",
      render: (text) => dayjs(text).format("DD MMM YYYY HH:mm"),
    },
    {
      title: "File Name",
      dataIndex: "file_name",
      key: "file_name",
    },
    {
      title: "Success Count",
      dataIndex: "success_count",
      key: "success_count",
      render: (count) => (
        <Tag color="green">{count || 0} Success</Tag>
      ),
    },
    {
      title: "Failure Count",
      dataIndex: "failure_count",
      key: "failure_count",
      render: (count) => (
        <Tag color={count > 0 ? "red" : "default"}>{count || 0} Failed</Tag>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const colorMap = {
          COMPLETED: "green",
          PROCESSING: "blue",
          FAILED: "red",
          PARTIAL: "orange",
        };
        return (
          <Tag color={colorMap[status] || "default"}>
            {status || "COMPLETED"}
          </Tag>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => showErrorDetails(record)}
          disabled={!record.failure_count || record.failure_count === 0}
        >
          View Errors
        </Button>
      ),
    },
  ];

  const fetchImportHistory = async (page = 1, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const response = await adminactions.loadImportHistory(
        page,
        pageSize,
        organization
      );

      if (response) {
        setImportHistory(response.results || response);
        setPagination({
          ...pagination,
          current: page,
          pageSize,
          total: response.count || response.length || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching import history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImportHistory(1);
  }, [organization]);

  const handleUpload = async (file) => {
    try {
      await adminactions.importAttendanceExcel(file, organization);
      fetchImportHistory(1); // Refresh history after upload
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  const handleDownloadSample = async () => {
    try {
      const blob = await adminactions.downloadAttendanceSample();
      if (blob) {
        fileDownload(blob, "attendance_sample.xlsx");
      }
    } catch (error) {
      console.error("Error downloading sample:", error);
    }
  };

  const showErrorDetails = (record) => {
    if (record.error_details) {
      try {
        const errors = JSON.parse(record.error_details);
        setErrorDetails(errors);
        setErrorModalVisible(true);
      } catch (e) {
        setErrorDetails([{ error: record.error_details }]);
        setErrorModalVisible(true);
      }
    }
  };

  const handleTableChange = (paginationConfig) => {
    // A new page size starts again from page 1
    const sizeChanged = paginationConfig.pageSize !== pagination.pageSize;
    fetchImportHistory(sizeChanged ? 1 : paginationConfig.current, paginationConfig.pageSize);
  };

  return (
    <div style={{ padding: 24 }}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Import Attendance">
            <Tabs defaultActiveKey="1">
              <TabPane tab="Upload File" key="1">
                <div style={{ maxWidth: 600 }}>
                  <Card
                    size="small"
                    title="Instructions"
                    style={{ marginBottom: 16 }}
                  >
                    <ol>
                      <li>Download the sample Excel file to see the required format</li>
                      <li>Fill in your attendance data following the sample format</li>
                      <li>Upload the completed file using the uploader below</li>
                      <li>View import results in the History tab</li>
                    </ol>
                    <Button
                      icon={<DownloadOutlined />}
                      onClick={handleDownloadSample}
                      style={{ marginTop: 8 }}
                    >
                      Download Sample File
                    </Button>
                  </Card>

                  <Card size="small" title="Upload Attendance File">
                    <ExcelUploader
                      onUpload={handleUpload}
                      uploadText="Click or drag Excel file to upload"
                      hintText="Support for .xlsx and .xls files only. Max size: 5MB"
                    />
                  </Card>
                </div>
              </TabPane>

              <TabPane tab="Import History" key="2">
                <Table
                  columns={historyColumns}
                  dataSource={importHistory}
                  rowKey="id"
                  loading={loading}
                  pagination={{ ...pagination, showSizeChanger: true }}
                  onChange={handleTableChange}
                />
              </TabPane>
            </Tabs>
          </Card>
        </Col>
      </Row>

      <Modal
        title="Import Error Details"
        open={errorModalVisible}
        onCancel={() => setErrorModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setErrorModalVisible(false)}>
            Close
          </Button>,
        ]}
        width={800}
      >
        <Table
          columns={[
            {
              title: "Row",
              dataIndex: "row",
              key: "row",
            },
            {
              title: "Error",
              dataIndex: "error",
              key: "error",
            },
          ]}
          dataSource={errorDetails}
          rowKey={(record, index) => index}
          pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        />
      </Modal>
    </div>
  );
};

export default ImportAttendance;
